import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { snakeToPascal } from '../../utils/caseconverters';
import styles from './StreamField.module.scss';

const ImageGallery = dynamic(() => import('../ImageGallery'));
const Faq = dynamic(() => import('../Faq'));
const RekAI = dynamic(() => import('../RekAI'));
const RawHtml = dynamic(() => import('../RawHtml'));
const ArticleList = dynamic(() => import('../ArticleList'));
const Cards = dynamic(() => import('../Cards'));
const Quote = dynamic(() => import('../Quote'));
const PlaceSection = dynamic(() => import('../PlaceSection'));
const PostSegment = dynamic(() => import('../PostSegment'));
const ShortFacts = dynamic(() => import('../ShortFacts'));
const CardFact = dynamic(() => import('../CardFact'));
const VideoSegment = dynamic(() => import('../VideoSegment'));
const Map = dynamic(() => import('../Map'));
const CTA = dynamic(() => import('../CTA'));
const CampaignPreamble = dynamic(() => import('../CampaignPreamble'));
const CampaignText = dynamic(() => import('../CampaignText'));
const CampaignPostSegment = dynamic(() => import('../CampaignPostSegment'));
const CampaignCards = dynamic(() => import('../CampaignCards'));
const CampaignVideoSegment = dynamic(() => import('../CampaignVideoSegment'));
const CampaignCTA = dynamic(() => import('../CampaignCTA'));
const CampaignEmbedded = dynamic(() => import('../CampaignEmbedded'));
const CampaignFaq = dynamic(() => import('../CampaignFaq'));
const CampaignModal = dynamic(() => import('../CampaignModal'));
const CampaignGrid = dynamic(() => import('../CampaignGrid'));
const CampaignDupes = dynamic(() => import('../CampaignDupes'));
const CampaignQuote = dynamic(() => import('../CampaignQuote'));

const Components = {
    ImageGallery: ImageGallery,
    FaqBlock: Faq,
    RekaiFaqBlock: RekAI,
    RichText: RawHtml,
    ArticleList: ArticleList,
    Cards: Cards,
    Quote: Quote,
    PlaceSection: PlaceSection,
    PostSegment: PostSegment,
    ShortFacts: ShortFacts,
    FactBox: CardFact,
    VideoSegment: VideoSegment,
    Map: Map,
    Cta: CTA,
    CampaignPreamble: CampaignPreamble,
    CampaignText: CampaignText,
    CampaignPostSegment: CampaignPostSegment,
    CampaignCards: CampaignCards,
    CampaignVideoSegment: CampaignVideoSegment,
    CampaignCta: CampaignCTA,
    CampaignEmbedded: CampaignEmbedded,
    CampaignFaq: CampaignFaq,
    CampaignModal: CampaignModal,
    CampaignGrid: CampaignGrid,
    CampaignDupes: CampaignDupes,
    CampaignQuote: CampaignQuote,
};

const StreamField = ({ items = [], pageType = '', id = '', ...restProps }) => {
    if (!items.length) {
        return null;
    }

    const dynamicComponents = items.map((item, index) => {
        item.component = snakeToPascal(item.type);
        const Component = Components[item.component];

        if (!Component) {
            return null;
        }

        const colors = item?.value?.colors || null;
        const itemId = item?.id || `streamfield-${index}`;
        const compId = id ? `${id}-${itemId}` : itemId;

        const classes = classNames(
            styles['StreamField__Component'],
            styles[`StreamField__Component--${item.component}`],
            {
                [styles['StreamField__Component--FirstItem']]: index === 0,
                [styles[`StreamField__Component--${colors}`]]: colors,
                [styles['StreamField__Component--NoColors']]: !colors,
            }
        );

        return (
            <div key={index} id={compId} className={classes}>
                <Component
                    id={compId}
                    pageType={pageType}
                    componentName={item.component}
                    {...restProps}
                    {...item.value}
                />
            </div>
        );
    });

    return <div className={styles['StreamField']}>{dynamicComponents}</div>;
};

StreamField.propTypes = {
    items: PropTypes.array,
    pageType: PropTypes.string,
};

export default StreamField;
